import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

const DEFAULT_TIMEOUT = 5000;

const Box = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  border-radius: 9px;
  padding: 9px 11px;
  font-weight: 500;
  font-size: 12px;

  .msg {
    flex: 1 1 0%;
    overflow-wrap: anywhere;
  }
`;

const Close = styled.button`
  display: inline-flex;
  padding: 2px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: inherit;
  opacity: 0.75;
  flex-shrink: 0;

  i {
    font-size: 12px;
  }

  &:hover,
  &:focus {
    outline: none;
    opacity: 1;
  }
`;

interface AlertProps {
  message: string;
  onDismiss: () => void;
}

// Dismissible cc-red banner. Auto-dismiss lives in useAlert, not here.
export const Alert = ({ message, onDismiss }: AlertProps): ReactElement => (
  <Box className='cc-red' role='alert'>
    <span className='msg'>{message}</span>
    <Close type='button' title='Dismiss' onClick={onDismiss}>
      <i className='far fa-times' />
    </Close>
  </Box>
);

interface AlertState {
  text: string;
  /** Bumped on every show() so re-triggering the same text restarts the timer. */
  nonce: number;
}

export interface AlertController {
  message: string | undefined;
  show: (text: string) => void;
  clear: () => void;
}

/*
 * Manages a single transient alert: show() sets the message, and it clears
 * itself after `timeout` ms (or via clear()). Re-showing the same text restarts
 * the timer thanks to the bumped nonce.
 */
export const useAlert = (timeout: number = DEFAULT_TIMEOUT): AlertController => {
  const [state, setState] = useState<AlertState>();

  const show = useCallback((text: string): void => {
    setState((prev) => ({ text, nonce: (prev?.nonce ?? 0) + 1 }));
  }, []);

  const clear = useCallback((): void => {
    setState(undefined);
  }, []);

  useEffect(() => {
    if (state === undefined) {
      return undefined;
    }

    const id = setTimeout(() => {
      setState(undefined);
    }, timeout);

    return (): void => {
      clearTimeout(id);
    };
  }, [state, timeout]);

  return { message: state?.text, show, clear };
};
